import { FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import Animated, {
  useAnimatedRef,
  useScrollViewOffset,
} from 'react-native-reanimated';
import { FadeView, theme } from '@sg/garnish';
import { ProductDetails } from '@sg/shared-fe/features/product-details';

import { messages } from '../../ProductDetails.messages.ts';
import {
  type BaseProduct,
  type ProductDetailsMappedContent,
} from '../../ProductDetails.types';
import { ProductNutritionData } from '../ProductNutritionData';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsReadyXS = (props: Props) => {
  const {
    baseProduct,
    content,
    hasNoSelectedLocation,
    isUnavailableProduct,
    isAddingToBag,
    navigateBack,
    navigateToLocationSearch,
    navigateToCustomization,
  } = props;

  const { formatMessage } = useIntl();

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffsetSV = useScrollViewOffset(scrollViewRef);

  const canAddToBag = !isUnavailableProduct && !hasNoSelectedLocation;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <FadeView
      show
      duration={theme.transitions.long}
      style={styles.outerContainer}
    >
      <ProductDetails.NavigationHeader
        title={formatMessage(messages.screenTitle)}
        foregroundColor={content.mobileHeaderForegroundColor}
        backgroundColor={content.mobileHeaderBackgroundColor}
        scrollOffsetSV={scrollOffsetSV}
        closeButtonA11yLabel={formatMessage(messages.goBack)}
        onCloseButtonPress={navigateBack}
      />

      <Animated.ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.contentContainer}
      >
        <ProductDetails.Header.Small.OuterContainer
          backgroundColor={content.mobileHeaderBackgroundColor}
          backgroundImageBaseUrl={content.mobileHeaderBackgroundImage?.fields?.file?.url} // prettier-ignore
        >
          <ProductDetails.Header.Small.Container>
            <ProductDetails.Header.Small.ContentContainer>
              <ProductDetails.Title color={content.mobileHeaderForegroundColor}>
                {baseProduct.name}
              </ProductDetails.Title>

              <ProductDetails.Text color={content.mobileHeaderForegroundColor}>
                {content.subheading ?? baseProduct.description}
              </ProductDetails.Text>
            </ProductDetails.Header.Small.ContentContainer>

            <ProductDetails.Banner.Small
              productName={baseProduct.name}
              productImageUrl={baseProduct.asset.url}
            />
          </ProductDetails.Header.Small.Container>

          {content.description ? (
            <ProductDetails.Text color={content.mobileHeaderForegroundColor}>
              {content.description}
            </ProductDetails.Text>
          ) : null}

          {content.nutritionistPhoto && content.nutritionistTitle ? (
            <ProductDetails.Nutritionist
              photo={content.nutritionistPhoto}
              title={content.nutritionistTitle}
              logo={content.nutritionistLogo}
              logoWidth={content.nutritionistLogoWidth}
              logoHeight={content.nutritionistLogoHeight}
              foregroundColor={content.mobileHeaderForegroundColor}
            />
          ) : null}
        </ProductDetails.Header.Small.OuterContainer>

        {content.superIngredients.length > 0 ? (
          <ProductDetails.ContentBlock withBorder>
            <ProductDetails.SectionTitle>
              <FormattedMessage {...messages.superIngredientsTitle} />
            </ProductDetails.SectionTitle>

            <ProductDetails.IngredientsStack>
              {content.superIngredients.map((entry) => (
                <ProductDetails.Ingredient.Container key={entry.name}>
                  <ProductDetails.Ingredient.Image
                    alt={entry.name}
                    baseUrl={entry.image.fields.file.url}
                  />

                  <ProductDetails.Ingredient.ContentContainer>
                    <ProductDetails.Ingredient.Title>
                      {entry.name}
                    </ProductDetails.Ingredient.Title>

                    <ProductDetails.Ingredient.Highlights
                      highlights={entry.highlights}
                    />
                  </ProductDetails.Ingredient.ContentContainer>
                </ProductDetails.Ingredient.Container>
              ))}
            </ProductDetails.IngredientsStack>
          </ProductDetails.ContentBlock>
        ) : null}

        <ProductDetails.ContentBlock>
          <ProductDetails.SectionTitle>
            <FormattedMessage {...messages.nutritionInfoTitle} />
          </ProductDetails.SectionTitle>

          <ProductNutritionData baseProduct={baseProduct} />
        </ProductDetails.ContentBlock>

        {isUnavailableProduct ? (
          <ProductDetails.ContentBlock>
            <ProductDetails.UnavailableProduct />
          </ProductDetails.ContentBlock>
        ) : null}
      </Animated.ScrollView>

      {canAddToBag ? null : (
        <ProductDetails.ChangeLocationButton
          hasNoSelectedLocation={hasNoSelectedLocation}
          onPress={navigateToLocationSearch}
        />
      )}

      {canAddToBag ? (
        <ProductDetails.AddToBagButton
          onPress={navigateToCustomization}
          isLoading={isAddingToBag}
        />
      ) : null}
    </FadeView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing['25'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  baseProduct: BaseProduct;
  content: ProductDetailsMappedContent;
  hasNoSelectedLocation: boolean;
  isUnavailableProduct: boolean;
  isAddingToBag: boolean;
  navigateBack: () => void;
  navigateToCustomization: () => void;
  navigateToLocationSearch: () => void;
};
